/*
* Order editing for authenticated users
*/

import { filterBy } from '@progress/kendo-data-query';
import * as _ from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { Alert, Button, ButtonToolbar, Modal, Pagination, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import { isNullOrUndefined } from 'util';
import * as MutateActions from '../actions/mutate';
import * as AppComp from '../common/app-component-base';
import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import CopyAddressToClipboard from '../common/copyaddresstoclipboard';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { EmailLinkComponent, NavigationLinks, NavLinkComponent } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import AppConfiguration from '../config/configuration';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';
import * as OrderCommon from './order-common';
import { OrderPricing } from './orderpricing';

export interface IOrderEditProps extends AppComp.IAppComponentProps {
    association?: any;
    associationClient?: any;
    associations?: any;
    clients?: any;
    companies?: any;
    navigateTo?: any;
    newOrder?: any;
    createNext3YearPlanSeqOrder?: any;
    orderChartDetail?: any;
    orderClient?: any;
    orderDetailOrder?: any;
    orderId?: any;
    orders?: any;
    serviceLevels?: any;
    orderServiceTypes?: any;
    statuses?: any;
    updateOrder?: any;
    deleteOrder?: any;
    users?: any;
}

export interface IOrderEditState extends AppComp.IAppComponentState {
    filterAssociation?: any;
    filterClient?: any;
    formChanges?: any;
    showModalCalculatePrices: boolean;
    showModalDeleteOrder: boolean;
    showModalNewOrder: boolean;
    showModalSaveChanges: boolean;
    showModalSetDueDate: boolean;
    showModalCreateNextPlanYearOrder: boolean;
    showWarningMissingPricingData: boolean;
    showWarningStaleAcceptedPrice: boolean;
    skipAssociation?: any;
    skipClient?: any;
}

class EditOrder extends AppComp.AppComponent<IOrderEditProps, IOrderEditState> {
    public MomentDateFormat: string = AppConfiguration.getMomentShortDateFormat();
    public MomentDateTimeFormat: string = AppConfiguration.getMomentDateTimeFormat();
    public originalFormDataCache = {};
    public readonly virtualizedDropdownPageSize: number = 20;

    public kendoFormWhiteBoxSectionProps = {};

    public constructor(props: IOrderEditProps) {
        super(props, 'app.order-detail.');

        this.state = {
            filterAssociation: undefined,
            filterClient: undefined,
            formChanges: {},
            showModalCalculatePrices: false,
            showModalDeleteOrder: false,
            showModalNewOrder: false,
            showModalSaveChanges: false,
            showModalSetDueDate: false,
            showModalCreateNextPlanYearOrder: false,
            showWarningMissingPricingData: false,
            showWarningStaleAcceptedPrice: false,
            skipAssociation: 0,
            skipClient: 0,
        };
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // Data transforms
    public formattedDate(date: any) {
        // Returns a formatted date, or empty

        return date ? moment(new Date(date)).format(this.MomentDateFormat) : '';
    }

    public extendOrder(order: any, orderReserveStudyChart?: any) {
        if (!order) { return {}; }

        let record = _.cloneDeep(order);

        // fye
        let preparedForFYE;
        const orderChartBeginYear = _.get(orderReserveStudyChart, 'beginYear');
        const assocFiscalYearEnd = _.get(record, 'association.details.fiscalYearEnd');

        if (orderChartBeginYear && assocFiscalYearEnd) {
            const fyeDateStr = `${orderChartBeginYear}/${assocFiscalYearEnd}`;
            const dateEndAssocFYE = new Date(fyeDateStr);
            const reportPeriodEndDateMoment = moment(dateEndAssocFYE);

            preparedForFYE = moment(reportPeriodEndDateMoment).toDate();
        }

        record.preparedForFYEDateFormatted = this.formattedDate(preparedForFYE);

        return record;
    }

    // Handlers
    public handleDeleteButton(e: any) {
        this.setState({ showModalDeleteOrder: true });
    }

    public handleDeleteButtonAction(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: _.pick(this.props.association, ['associationId', '_id']) },
            { order: _.pick(this.props.orderDetailOrder, ['orderId', '_id']) },
        );

        this.props.deleteOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.setState({ formChanges: {}, showModalDeleteOrder: false });

                this.props.navigateTo(NavigationLinks.associationDetail(_.get(this.props, 'association.associationId')));
            }
        });
    }

    public handleCancelAndCloseModalDeleteOrder() {
        this.setState({ showModalDeleteOrder: false });
    }

    public handleShowModalSetDueDate(e: any, formData: any) {
        this.setState({ showModalSetDueDate: true });
    }

    public handleCloseModalSetDueDate() {
        this.setState({ showModalSetDueDate: false });
    }

    public handleShowModalCalculatePrices(e: any, formData: any) {
        this.setState({ showModalCalculatePrices: true });
    }

    public handleCloseModalCalculatePrices() {
        this.setState({ showModalCalculatePrices: false });
    }

    public handleShowModalCreateNextPlanYearOrder(e: any, formData: any) {
        this.setState({ showModalCreateNextPlanYearOrder: true });
    }

    public handleCloseModalCreateNextPlanYearOrder() {
        this.setState({ showModalCreateNextPlanYearOrder: false });
    }

    public handleResearchBidsButton(e: any) {
        this.props.navigateTo(NavigationLinks.researchBids());
    }

    public handleOrderBidLetterPdfButton(e: any) {
        this.props.navigateTo(NavigationLinks.orderBidLetterPdf(this.props.orderId));
    }

    public handleOrderBidEnvelopePdfButton(e: any) {
        this.props.navigateTo(NavigationLinks.orderBidEnvelopePdf(this.props.orderId));
    }

    public handleViewAssociationButton(e: any) {
        this.props.navigateTo(NavigationLinks.associationDetail(Formatters.getInt(this.props, 'association.associationId')));
    }

    public handleSaveButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { order: _.pick(this.props.orderDetailOrder, ['orderId', '_id']) },
            this.state.formChanges
        );

        // console.log(`handleSaveButton`, { changeData });

        this.props.updateOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};
                this.setState({ formChanges: {}, showModalSaveChanges: false, showModalNewOrder: false, showModalCreateNextPlanYearOrder: false, showWarningMissingPricingData: false, showWarningStaleAcceptedPrice: false });
            } else {
                // Error during creation...
            }
        });
    }

    public handleShowModalNewOrder(e: any, formData: any) {
        this.setState({ showModalNewOrder: true });
    }

    public handleCloseModalNewOrder() {
        this.setState({ showModalNewOrder: false, formChanges: { newOrder: { associationId: undefined, clientId: -1 } } });
    }

    public handleCreateOrderForAssocButton(e: any, formData: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: { associationId: _.get(formData, 'associationId', -1) } }, // _.get(this.state, 'formChanges.newOrder.associationId', -1) } },
            { client: { clientId: -1 } }, // _.get(this.state, 'formChanges.newOrder.clientId') } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                this.setState({ showModalNewOrder: false });

                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreateOrderOpenModalButton(e: any, formData: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: { associationId: _.get(formData, 'newOrder.associationId', -1) } }, // _.get(this.state, 'formChanges.newOrder.associationId', -1) } },
            { client: { clientId: -1 } }, // _.get(this.state, 'formChanges.newOrder.clientId') } },
        );

        this.props.newOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                this.setState({ showModalNewOrder: false });

                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleCreateNextPlanYearOrderModalButton(e: any, formData: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { association: { associationId: _.get(formData, 'associationId', -1) } },
            { order: { orderId: _.get(formData, 'orderId', -1) } },
            { client: { clientId: -1 } },
            { createNextPlanYearOrder: true }
        );

        // console.log(`handleCreateNextPlanYearOrderModalButton`, { e, formData, changeData, token });

        this.props.createNext3YearPlanSeqOrder(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                this.setState({ showModalCreateNextPlanYearOrder: false });

                // FORCE RELOAD
                const newOrderId = _.get(response, 'body.orderId');

                this.props.navigateTo(NavigationLinks.orderEdit(newOrderId));
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    public handleAssociationDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipAssociation: 0,
            filterAssociation: event.filter,
        });
    }

    public handleAssociationDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipAssociation: event.page.skip
        });
    }

    public handleClientDropdownFilterChange(event: any, formData: any) {
        this.setState({
            skipClient: 0,
            filterClient: event.filter,
        });
    }

    public handleClientDropdownPageChange(event: any, formData: any) {
        this.setState({
            skipClient: event.page.skip
        });
    }

    public handleSetPricesButton(e: any) {
        // Overwrite order price matrix with default values, if level 1 economy price has been input
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);

        const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

        let orderChanges = _.stubArray();
        const pricing = _.get(formData, 'pricingMatrix', []);

        if (AppUtilities.isArrayWithLength(pricing)) {
            let firstValidPricingLevel = '';

            if (OrderPricing.getOrderPricingLevel1Economy(pricing) > 0) {
                firstValidPricingLevel = '1';
            } else if (OrderPricing.getOrderPricingLevel2Economy(pricing) > 0) {
                firstValidPricingLevel = '2';
            } else if (OrderPricing.getOrderPricingLevel3Economy(pricing) > 0) {
                firstValidPricingLevel = '3';
            }

            if (firstValidPricingLevel) {
                const newPricingMatrix = _.cloneDeep(OrderPricing.buildPriceMatrixAndSetPrices(_.cloneDeep(pricing), firstValidPricingLevel));

                if (_.size(newPricingMatrix) && !_.isEqual(newPricingMatrix, pricing)) {
                    // orderChanges.push({ fieldName: 'pricingMatrix', newValue: newPricingMatrix });

                    // As per Rob, move 3yr pricing setting into main order price setting
                    if (AppUtilities.isArrayWithLength(newPricingMatrix)) {
                        const opL1Eco = OrderPricing.getOrderPricingLevel1Economy(newPricingMatrix);
                        if (opL1Eco) {
                            const opL1S = OrderPricing.getOrderPricingLevel1Standard(newPricingMatrix);
                            const opL1R = OrderPricing.getOrderPricingLevel1Rush(newPricingMatrix);

                            const diffL1StandardL1Eco = opL1S - opL1Eco;
                            const diffL1RushL1Eco = opL1R - opL1Eco;

                            const npmLevL5 = OrderPricing.getOrderPricingMatrixItem(newPricingMatrix, 5);
                            // the following cost adjustments were moved into the buildPriceMatrixAndSetPrices call above
                            const npmLevL5Base = Formatters.getFloat(npmLevL5, 'economyPrice');
                            const npmLevL5Standard = Formatters.getFloat(npmLevL5, 'standardPrice'); // npmLevL5Base + diffL1StandardL1Eco;
                            const npmLevL5Rush = Formatters.getFloat(npmLevL5, 'rushPrice'); // npmLevL5Base + diffL1RushL1Eco;

                            if (npmLevL5) {
                                // Set 3 year plan prices in orderServicesPricingMatrix
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.economyPricing.price', newValue: Formatters.getFloat(npmLevL5, 'economyPrice') });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.standardPricing.price', newValue: npmLevL5Standard });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.rushPricing.price', newValue: npmLevL5Rush });

                                // Set 3 year plan year numbers
                                const prepFYEDate = _.get(formData, 'preparedForFYEDate') ? new Date(formData.preparedForFYEDate) : new Date(); // null;
                                const prepFYEDateYear = prepFYEDate ? moment(prepFYEDate).year() : moment().year(); // null;
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.economyPricing.pricingYear', newValue: prepFYEDateYear + 1 });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.economyPricing.pricingYearDate', newValue: new Date(prepFYEDateYear + 1, 0, 1, 1, 1, 1, 1) });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.standardPricing.pricingYear', newValue: prepFYEDateYear + 2 });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.standardPricing.pricingYearDate', newValue: new Date(prepFYEDateYear + 2, 0, 1, 1, 1, 1, 1) });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.rushPricing.pricingYear', newValue: prepFYEDateYear + 3 });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.rushPricing.pricingYearDate', newValue: new Date(prepFYEDateYear + 3, 0, 1, 1, 1, 1, 1) });

                                // Set 3 year plan year count
                                let fdOSPMPlanYearSeqNum = Formatters.getInt(formData, 'orderServicesPricingMatrix.planYearSequenceNum', 1);
                                if (fdOSPMPlanYearSeqNum <= 0) { fdOSPMPlanYearSeqNum = 1; }
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.planYearSequenceNum', newValue: fdOSPMPlanYearSeqNum });

                                // Set 3 year plan levels due
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.economyPricing.levelDueText', newValue: this.appText('pricing-3-year-plan-level-due.site-visit') });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.standardPricing.levelDueText', newValue: this.appText('pricing-3-year-plan-level-due.no-site-visit') });
                                orderChanges.push({ fieldName: 'orderServicesPricingMatrix.rushPricing.levelDueText', newValue: this.appText('pricing-3-year-plan-level-due.no-site-visit') });
                            }
                        }
                    }
                    orderChanges.push({ fieldName: 'pricingMatrix', newValue: newPricingMatrix });
                }
            }
        }

        // Set any missing date field values
        if (!_.get(formData, 'followUpDate')) {
            const newDate = moment().add(1, 'month').toDate();
            orderChanges.push({ fieldName: 'followUpDate', newValue: newDate });
        }

        if (!_.get(formData, 'reminderBidDate')) {
            const newDate = moment().add(1, 'year').toDate();
            orderChanges.push({ fieldName: 'reminderBidDate', newValue: newDate });
        }

        if (!_.get(formData, 'bidDate')) {
            const newDate = moment().toDate();
            orderChanges.push({ fieldName: 'bidDate', newValue: newDate });
        }

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges, showModalCalculatePrices: false });
        } else {
            this.setState({ showModalCalculatePrices: false });
        }
    }

    public handleSetDueDateButton(e: any) {
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);

        const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

        let orderChanges = _.stubArray();

        const newValue = moment(formData.startDate).add(formData.setDueDateWeeks, 'week').toDate();
        orderChanges.push({ fieldName: 'dueDate', newValue: newValue });

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges, showModalSetDueDate: false });
        } else {
            this.setState({ showModalSetDueDate: false });
        }
    }

    public handleSetAcceptedPriceOverrideCheckbox(e: any) {
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);

        const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

        let orderChanges = _.stubArray();

        const newValue = e.value;
        orderChanges.push({ fieldName: 'overrideAcceptedPrice', newValue: newValue });

        if (newValue) {
            this.setState({ showWarningStaleAcceptedPrice: false });
        }

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        } else {
            // this.setState({ showModalSetDueDate: false });
        }
    }

    public canUpdateExistingValue(obj: any, path: any, updateExistingSetting: boolean) {
        let result = false;

        const prevValue = _.get(obj, path, undefined);

        if (updateExistingSetting) {
            result = true;
        } else {
            if (!prevValue) {
                result = true;
            }
        }

        return result;
    }

    public handleChangeStatus(e: any, obj: any) {
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);

        const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

        let orderChanges = _.stubArray();

        const newValue = Number(Formatters.getInt(e, 'target.value'));

        orderChanges.push({ fieldName: 'status.statusId', newValue: newValue });

        const config = _.find(OrderPricing.StatusUpdateFieldsConfig, ['statusId', newValue]);

        if (config) {
            if (_.size(_.get(config, 'updateDateFields', [])) > 0) {
                const dateUpdates = _.get(config, 'updateDateFields', []);

                dateUpdates.forEach((c) => {
                    const getFieldValue = Formatters.getInt(c, 'addDays');
                    const getFieldKey = Formatters.getStr(c, 'fieldKey');
                    const getUpdateExisting = _.get(c, 'updateExisting', true);

                    if (this.canUpdateExistingValue(formData, getFieldKey, getUpdateExisting)) {
                        const newDate = moment(new Date()).add(getFieldValue, 'days').toDate();
                        orderChanges.push({ fieldName: getFieldKey, newValue: newDate });
                    }
                });
            }
        }

        // console.log(`handleChangeStatus to newValue ${newValue}`, orderChanges);

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handleChangeServiceLevel(e: any, obj: any) {
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);

        const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

        let orderChanges = _.stubArray();

        const newValue = Number(Formatters.getInt(e, 'target.value'));

        orderChanges.push({ fieldName: 'serviceLevelId', newValue: newValue });
        orderChanges.push({ fieldName: 'serviceLevel.serviceLevelId', newValue: newValue });

        const formDataOrderPricingMatrix = _.get(formData, 'pricingMatrix');

        const config = _.find(OrderPricing.ServiceLevelUpdateFieldsConfig, ['serviceLevelId', newValue]);
        // console.log(`handleChangeServiceLevel ServiceLevelUpdateFieldsConfig config for serviceLevelId/newValue ${newValue}`, {
        //     formDataOrderPricingMatrix,
        //     config,
        //     e,
        //     newValue,
        //     orderpricing_servicelevelupdatefieldsconfig: OrderPricing.ServiceLevelUpdateFieldsConfig,
        // });

        if (config && _.size(formDataOrderPricingMatrix) > 0) {
            if (_.get(config, 'updateAcceptedPrice.update', false) === true) {
                const getLevel = Formatters.getInt(config, 'updateAcceptedPrice.pricingLevel');
                const getFieldKey = Formatters.getStr(config, 'updateAcceptedPrice.priceFieldKey');
                const newMatrixPrice = Number(OrderPricing.getOrderLevelPricing(formDataOrderPricingMatrix, getLevel, getFieldKey));
                const prevAcceptedPrice = Number(Formatters.getFloat(formData, 'acceptedPrice'));

                if (newMatrixPrice !== prevAcceptedPrice) {
                    orderChanges.push({ fieldName: 'acceptedPrice', newValue: newMatrixPrice });
                }
                orderChanges.push({ fieldName: 'overrideAcceptedPrice', newValue: false });
            }

            if (_.size(_.get(config, 'updateDateFields', [])) > 0) {
                const dateUpdates = _.get(config, 'updateDateFields', []);

                dateUpdates.forEach((c) => {
                    const getFieldValue = Formatters.getInt(c, 'addDays');
                    const getFieldKey = Formatters.getStr(c, 'fieldKey');
                    const getUpdateExisting = _.get(c, 'updateExisting', true);

                    if (this.canUpdateExistingValue(formData, getFieldKey, getUpdateExisting)) {
                        const newDate = moment(new Date()).add(getFieldValue, 'days').toDate();

                        orderChanges.push({ fieldName: getFieldKey, newValue: newDate });
                    }
                });
            }

            if (_.size(_.get(config, 'updateStatusFields', [])) > 0) {
                const statusUpdates = _.get(config, 'updateStatusFields', []);

                statusUpdates.forEach((c) => {
                    const getFieldValue = Formatters.getInt(c, 'fieldValue');
                    const getFieldKey = Formatters.getStr(c, 'fieldKey');
                    const getUpdateExisting = _.get(c, 'updateExisting', true);

                    if (this.canUpdateExistingValue(formData, getFieldKey, getUpdateExisting)) {
                        orderChanges.push({ fieldName: getFieldKey, newValue: getFieldValue });
                    }
                });
            }
        }

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handlePricingMatrixChange(e: any, obj: any) {
        const {
            orderDetailOrder,
            orderId,
            orders,
            association,
            users,
            statuses,
            serviceLevels,
            associationClient,
            orderClient,
            associations,
            clients,
            orderServiceTypes,
            ...rest } = this.props;

        let orderChanges = _.stubArray();

        const fieldName = Formatters.getStr(e, 'target.props.name');
        const newValue = Number(Formatters.getFloat(e, 'target.value'));

        orderChanges.push({ fieldName: fieldName, newValue: newValue });

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handle3YearPlanPricingMatrixChange(e: any, obj: any) {
        const {
            orderDetailOrder,
            orderId,
            orders,
            association,
            users,
            statuses,
            serviceLevels,
            associationClient,
            orderClient,
            associations,
            clients,
            orderServiceTypes,
            ...rest } = this.props;

        let orderChanges = _.stubArray();

        const fieldName = Formatters.getStr(e, 'target.props.name');
        const newValue = Number(Formatters.getFloat(e, 'target.value'));

        let pricingMatrixField = ''; // for applying change to both order services pricing matrix and regular pricing matrix

        if (fieldName === 'orderServicesPricingMatrix.economyPricing.price') {
            pricingMatrixField = 'pricingMatrix[3].economyPrice';
        }
        if (fieldName === 'orderServicesPricingMatrix.standardPricing.price') {
            pricingMatrixField = 'pricingMatrix[3].standardPrice';
        }
        if (fieldName === 'orderServicesPricingMatrix.rushPricing.price') {
            pricingMatrixField = 'pricingMatrix[3].rushPrice';
        }

        orderChanges.push({ fieldName: fieldName, newValue: newValue }); // apply to order services pricing matrix
        orderChanges.push({ fieldName: pricingMatrixField, newValue: newValue }); // apply to regular pricing matrix

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handle3YearPlanPricingMatrixYearChange(e: any, obj: any) {
        const {
            orderDetailOrder,
            orderId,
            orders,
            association,
            users,
            statuses,
            serviceLevels,
            associationClient,
            orderClient,
            associations,
            clients,
            orderServiceTypes,
            ...rest } = this.props;

        let orderChanges = _.stubArray();

        const fieldName = Formatters.getStr(e, 'target.props.name');
        const newValue = _.get(e, 'target.value');

        const newValIsDate = _.isDate(newValue);
        if (!newValIsDate) { return; }

        orderChanges.push({ fieldName: fieldName, newValue: newValue });

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handle3YearPlanPricingMatrixPlanYearChange(e: any, obj: any) {
        const {
            orderDetailOrder,
            orderId,
            orders,
            association,
            users,
            statuses,
            serviceLevels,
            associationClient,
            orderClient,
            associations,
            clients,
            orderServiceTypes,
            ...rest } = this.props;

        let orderChanges = _.stubArray();

        const fieldName = Formatters.getStr(e, 'target.props.name');
        const newValue = Number(Formatters.getInt(e, 'target.value'));

        orderChanges.push({ fieldName: fieldName, newValue: newValue });

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: 'orderServicesPricingMatrix.planYearSequenceNum',
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    public handle3YearPlanPricingMatrixLevelDueChange(e: any, obj: any) {
        const {
            orderDetailOrder,
            orderId,
            orders,
            association,
            users,
            statuses,
            serviceLevels,
            associationClient,
            orderClient,
            associations,
            clients,
            orderServiceTypes,
            ...rest } = this.props;

        let orderChanges = _.stubArray();

        const fieldName = Formatters.getStr(e, 'target.name');
        const newValue = _.get(e, 'target.value');

        orderChanges.push({ fieldName: fieldName, newValue: newValue });

        if (_.size(orderChanges)) {
            const changes = _.map(orderChanges, (o) => {
                return this.getFormChanges(
                    {
                        target: {
                            props: {
                                name: _.get(o, 'fieldName')
                            }
                        },
                        value: _.get(o, 'newValue')
                    },
                    null
                );
            });

            const changesMapped: any = _.map(changes, 'formChangesResult');

            let cloneFormChanges = _.cloneDeep(this.state.formChanges);
            changesMapped.forEach((o) => _.merge(cloneFormChanges, o));

            this.setState({ formChanges: cloneFormChanges });
        }
    }

    // Helpers
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`, { e });
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public modifyServiceLevelNames(data: any) {
        let result = _.cloneDeep(data);
        return result;
    }

    public getServiceLevelPricingById(id: number, pricingMatrix: any) {
        const config = _.find(OrderPricing.ServiceLevelUpdateFieldsConfig, ['serviceLevelId', id]);
        const getLevel = Formatters.getInt(config, 'updateAcceptedPrice.pricingLevel');
        const getFieldKey = Formatters.getStr(config, 'updateAcceptedPrice.priceFieldKey');
        const matrixPrice = Number(OrderPricing.getOrderLevelPricing(pricingMatrix, getLevel, getFieldKey));
        return matrixPrice;
    }

    public getFormData(orderIn: any, orderId: any, orders: any, users: any, statuses: any, services: any, associationClient: any, orderClient: any, associations: any, clients: any, companies: any, orderServiceTypes: any) {
        const orderRaw = _.merge(orderIn, this.state.formChanges);

        const rawPricingMatrix = _.get(orderRaw, 'pricingMatrix');
        const rawOrderServicesPricingMatrix = _.get(orderRaw, 'orderServicesPricingMatrix');

        const ospmMissing = (rawOrderServicesPricingMatrix === undefined) ? true : false;
        const formDataOrderPricingMatrix = (ospmMissing) ? rawPricingMatrix : _.map(_.cloneDeep(rawPricingMatrix), (o, i) => {
            if ((_.parseInt(i) === 3) && (o.economyPrice === null || o.standardPrice === null || o.rushPrice === null)) {
                _.set(o, 'economyPrice', orderRaw.orderServicesPricingMatrix.economyPricing.price);
                _.set(o, 'standardPrice', orderRaw.orderServicesPricingMatrix.standardPricing.price);
                _.set(o, 'rushPrice', orderRaw.orderServicesPricingMatrix.rushPricing.price);
            }
            return o;
        });
        const overrideAcceptedPrice = _.get(orderRaw, 'overrideAcceptedPrice', false);

        const order = orderRaw;
        let staleAcceptedPrice = false;
        if (isNullOrUndefined(order.serviceLevel) || isNullOrUndefined(order.acceptedPrice)) {
            // new order / not yet set / do nothing
        } else if (order.acceptedPrice !== this.getServiceLevelPricingById(order.serviceLevelId, formDataOrderPricingMatrix)) {
            if (!overrideAcceptedPrice) {
                staleAcceptedPrice = true;
            }
        }

        if (this.state.showWarningStaleAcceptedPrice === false) {
            if (staleAcceptedPrice) {
                this.setState({ showWarningStaleAcceptedPrice: true });
            }
        }

        if (this.state.showWarningStaleAcceptedPrice === true) {
            if (!staleAcceptedPrice) {
                this.setState({ showWarningStaleAcceptedPrice: false });
            }
        }

        if (this.state.showWarningMissingPricingData === false) {
            if (ospmMissing) {
                this.setState({ showWarningMissingPricingData: true });
            }
        }

        const usersFiltered = (_.size(users)) ?
            _.filter(users, (user) => {
                if ((!user.inactive) && (_.some(user.roles, { name: 'employee' }) || _.some(user.roles, { name: 'administrator' }))) {
                    return user;
                }
            }) : [];

        const orderUserOptions = (_.size(usersFiltered)) ? usersFiltered.map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.composeUsernameLabel(c, true) })) : [];

        const orderStatusOptions = (_.size(statuses)) ?
            _.orderBy(statuses.map((c) => _.merge({}, { value: Formatters.getInt(c, 'statusId'), text: Formatters.getStr(c, 'name') })), 'value')
            : [];

        const orderCompanyOptions = (_.size(companies)) ?
            _.orderBy(companies.map((c) => _.merge({}, { value: Formatters.getInt(c, 'companyId'), text: Formatters.getStr(c, 'name') })), 'value')
            : [];

        const servicesTransformed = _.map(services, (c) => {
            let resultServiceTransformed = _.merge({ sortGroupIndex: OrderCommon.OrderServiceLevelGroups.Miscellaneous.sort }, c);

            let servNameRaw = Formatters.getStr(c, 'name');
            let servNameSplit = _.split(servNameRaw, ' ');
            let servNameFirst = Formatters.getStr(servNameSplit, '[0]');
            const servNameGroupSort = OrderCommon.OrderServiceLevelSortFromGroup(servNameFirst);

            resultServiceTransformed.sortIndexGroup = servNameGroupSort + (c.sort * 1000);

            // console.log(`servicesTransformed`, { c });

            return resultServiceTransformed;
        });

        const orderServiceOptions = (_.size(servicesTransformed)) ?
            _.orderBy(servicesTransformed.map((c) => _.merge({}, { sortIndexGroup: Formatters.getInt(c, 'sortIndexGroup'), value: Formatters.getInt(c, 'serviceLevelId'), text: Formatters.getStr(c, 'name') })), 'sortIndexGroup')
            : [];

        const orderServiceTypesOptions = (_.size(orderServiceTypes)) ?
            // _.orderBy(orderServiceTypes.map((c) => _.merge({}, { value: Formatters.getStr(c, '_id'), text: Formatters.getStr(c, 'name') })), 'value')
            _.orderBy(orderServiceTypes.map((c) => _.merge({}, { value: Formatters.getStr(c, 'name'), text: Formatters.getStr(c, 'name') })), 'value')
            : [];

        const orderAssociationClientContactOptions = (_.size(_.get(associationClient, 'contacts', []))) ?
            _.orderBy(associationClient.contacts, ['lastName', 'firstName'], ['asc', 'asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'contactId'), text: Formatters.composeClientContactFormal(c) }))
            : [];

        const orderOrderClientContactOptions = (_.size(_.get(orderClient, 'contacts', []))) ?
            _.orderBy(orderClient.contacts, ['lastName', 'firstName'], ['asc', 'asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'contactId'), text: Formatters.composeClientContactFormal(c) }))
            : [];

        let newOrderAssociationOptions = (_.size(associations)) ?
            _.orderBy(associations, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'associationId'), text: Formatters.composeAssociationNameLabel(c, true) }))
            : [];
        if (this.state.filterAssociation) {
            newOrderAssociationOptions = filterBy(newOrderAssociationOptions, this.state.filterAssociation);
        }

        let newOrderClientOptions = (_.size(clients)) ?
            _.orderBy(clients, ['name'], ['asc']).map((c) => _.merge({}, { value: Formatters.getInt(c, 'clientId'), text: Formatters.composeClientNameLabel(c, true) }))
            : [];
        if (this.state.filterClient) {
            newOrderClientOptions = filterBy(newOrderClientOptions, this.state.filterClient);
        }

        const orderDashTextLetter = Formatters.getStr(order, 'orderId') + '-' + Formatters.getOrderDashLetter(order);
        const orderDashTextNumber = Formatters.getStr(order, 'orderId') + '-' + Formatters.getInt(order, 'dash');

        let result = {
            association: _.get(order, 'association'),
            associationId: Formatters.getInt(order, 'associationId'),

            company: {
                companyId: Formatters.getInt(order, 'company.companyId'),
            },

            client: _.get(order, 'client'),
            clientId: Formatters.getInt(order, 'clientId'),

            orderId: orderId,

            orderDashNumberText: `${orderDashTextNumber} / ${orderDashTextLetter}`, // Formatters.getStr(order, 'orderId') + '-' + Formatters.getOrderDashLetter(order),
            orderDashNumberNum: Formatters.getInt(order, 'dash'),
            orderDashNumberLet: Formatters.getOrderDashLetter(order),
            orderDashTextLetter: orderDashTextLetter,
            orderDashTextNumber: orderDashTextNumber,

            user: _.get(order, 'user'),
            siteInspector: _.get(order, 'siteInspector'),
            orderUserOptions: (_.size(orderUserOptions)) ? orderUserOptions : [],

            bidDate: _.get(order, 'bidDate'),
            bidSentDate: _.get(order, 'bidSentDate'),
            completionDate: _.get(order, 'completionDate'),
            dueDate: _.get(order, 'dueDate'),
            followUpDate: _.get(order, 'followUpDate'),
            preparedForFYEDate: _.get(order, 'preparedForFYEDate'),
            preparedForFYEDateFormatted: _.get(order, 'preparedForFYEDateFormatted'),
            reminderBidDate: _.get(order, 'reminderBidDate'),
            siDueDate: _.get(order, 'siDueDate'),
            startDate: _.get(order, 'startDate'),
            wonDate: _.get(order, 'wonDate'),

            bidLetter3YearPlanLanguage: _.get(order, 'bidLetter3YearPlanLanguage', true),
            feedback: _.get(order, 'feedback'),
            sampleSent: _.get(order, 'sampleSent'),
            unsolicitedBid: _.get(order, 'unsolicitedBid'),
            contractReceived: _.get(order, 'contractReceived'),
            enteredInQuickbooks: _.get(order, 'enteredInQuickbooks'),
            invoiceSentInQuickbooks: _.get(order, 'invoiceSentInQuickbooks'),
            inspectorPaid: _.get(order, 'inspectorPaid'),
            worksheetReceived: _.get(order, 'worksheetReceived'),
            twoOptionPricing: _.get(order, 'twoOptionPricing', false),

            acceptedPrice: Formatters.getFloat(order, 'acceptedPrice'),
            overrideAcceptedPrice: _.get(order, 'overrideAcceptedPrice', false),
            status: _.get(order, 'status'),
            serviceLevel: _.get(order, 'serviceLevel'),
            serviceLevelId: Formatters.getInt(order, 'serviceLevelId'),

            contact: _.get(order, 'contact'),
            contactId: Formatters.getInt(order, 'contactId'),

            pricingMatrix: formDataOrderPricingMatrix, // _.get(order, 'pricingMatrix'),
            orderServicesPricingMatrix: _.get(order, 'orderServicesPricingMatrix', {}), // child doc with rushPricing, etc

            // Order Pricing: 3 year plan future years
            pricingMatrix3YearPlan: [
                { futureYearIndex: 1, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
                { futureYearIndex: 2, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
                { futureYearIndex: 3, year: 0, planYearDate: new Date(), levelDue: '', disabled: true },
            ],

            orderNotesAdd: Formatters.getStr(order, 'orderNotesAdd'),
            orderNotesDelete: _.get(order, 'orderNotesDelete', []),
            orderNotes: _.get(order, 'notes', []),

            followUpNotesAdd: Formatters.getStr(order, 'followUpNotesAdd'),
            followUpNotesDelete: _.get(order, 'followUpNotesDelete', []),
            followUpNotes: _.get(order, 'followUpNotes', []),

            orderCompanyOptions: (_.size(orderCompanyOptions)) ? orderCompanyOptions : [],
            orderStatusOptions: (_.size(orderStatusOptions)) ? orderStatusOptions : [],
            orderServiceOptions: (_.size(orderServiceOptions)) ? orderServiceOptions : [],
            orderServiceTypesOptions: (_.size(orderServiceTypesOptions)) ? orderServiceTypesOptions : [],
            orderAssociationClientContactOptions: (_.size(orderAssociationClientContactOptions)) ? orderAssociationClientContactOptions : [],
            orderOrderClientContactOptions: (_.size(orderOrderClientContactOptions)) ? orderOrderClientContactOptions : [],

            newOrder: {
                associationId: Formatters.getInt(this.state.formChanges, 'newOrder.associationId', -1), // Formatters.getInt(order, 'associationId', -1)),
                clientId: -1,
                associationOptions: (_.size(newOrderAssociationOptions)) ? newOrderAssociationOptions : [],
                clientOptions: (_.size(newOrderClientOptions)) ? newOrderClientOptions : [],
            },

            setDueDateWeeks: _.get(order, 'setDueDateWeeks', 1),
            newDueDate: new Date(),

            isPreliminaryStudy: _.get(orderIn, 'isPreliminaryStudy', false) ? true : false,
        };

        const prepFYEDate = _.get(result, 'preparedForFYEDate') ? new Date(result.preparedForFYEDate) : new Date(); // null;
        const prepFYEDateYear = prepFYEDate ? moment(prepFYEDate).year() : moment().year(); // null;

        const pricingMatrixKeys = [
            OrderPricing.OrderServicesPricingMatrixFields.economyPricing,
            OrderPricing.OrderServicesPricingMatrixFields.standardPricing,
            OrderPricing.OrderServicesPricingMatrixFields.rushPricing,
        ];

        // _.forEach(pricingMatrixKeys, (k) => {
        //     const curPricingYear = Formatters.getInt(result, `orderServicesPricingMatrix.${k}.pricingYear`);
        //     if (curPricingYear > 0) {
        //         _.set(result, `orderServicesPricingMatrix.${k}.pricingYearDate`, new Date(curPricingYear, 0, 1, 1, 1, 1, 1));
        //     }
        // });

        if (prepFYEDateYear) {
            result.pricingMatrix3YearPlan[0].year = prepFYEDateYear + 0;
            result.pricingMatrix3YearPlan[0].planYearDate = new Date(result.pricingMatrix3YearPlan[0].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[0].levelDue = this.appText('pricing-3-year-plan-level-due.site-visit');
            result.pricingMatrix3YearPlan[0].disabled = false;

            result.pricingMatrix3YearPlan[1].year = prepFYEDateYear + 1;
            result.pricingMatrix3YearPlan[1].planYearDate = new Date(result.pricingMatrix3YearPlan[1].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[1].levelDue = this.appText('pricing-3-year-plan-level-due.no-site-visit');
            result.pricingMatrix3YearPlan[1].disabled = false;

            result.pricingMatrix3YearPlan[2].year = prepFYEDateYear + 2;
            result.pricingMatrix3YearPlan[2].planYearDate = new Date(result.pricingMatrix3YearPlan[2].year, 0, 1, 1, 1, 1, 1);
            result.pricingMatrix3YearPlan[2].levelDue = this.appText('pricing-3-year-plan-level-due.no-site-visit');
            result.pricingMatrix3YearPlan[2].disabled = false;
        }

        // New due date
        if (result.startDate && result.setDueDateWeeks) {
            _.set(result, 'newDueDate', moment(result.startDate).add(result.setDueDateWeeks, 'week').toDate());
        }

        // Order notes
        if (_.size(result.orderNotes)) {
            result.orderNotes = _.filter(result.orderNotes, (o) => Formatters.getStr(o, 'notes') !== '');
            if (_.size(result.orderNotes)) {
                result.orderNotes = _.orderBy(result.orderNotes, ['created'], ['desc']);
            }
        }

        if (_.size(result.orderNotesDelete)) {
            result.orderNotes = _.omitBy(result.orderNotes, (o) => _.includes(result.orderNotesDelete, o.objectId));
        }

        // Follow up notes
        if (_.size(result.followUpNotes)) {
            result.followUpNotes = _.filter(result.followUpNotes, (o) => Formatters.getStr(o, 'notes') !== '');
            if (_.size(result.followUpNotes)) {
                result.followUpNotes = _.orderBy(result.followUpNotes, ['created'], ['desc']);
            }
        }

        if (_.size(result.followUpNotesDelete)) {
            result.followUpNotes = _.omitBy(result.followUpNotes, (o) => _.includes(result.followUpNotesDelete, o.objectId));
        }

        const fc = _.get(this.state, 'formChanges');

        const res = _.merge(result, this.state.formChanges);

        // console.log(`getFormData merge`, {
        //     result,
        //     fc,
        //     res
        // });

        return res;
    }

    public getFormChangesCount() {
        return _.size(this.state.formChanges);
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;
        // Props values
        const pa = Formatters.getInt(this.props, 'orderId');

        // Cached values
        const ca = Formatters.getInt(this.originalFormDataCache, 'orderId');

        // Determine if formdata state and cache needs reset due to changing associd or invid
        if (pa && ca) {
            resetFormData = (pa !== ca) ? true : false;
        }

        return resetFormData;
    }

    // Renderers
    public renderHeaderButtonToolbar = (order: any, orderId: any, allOrders: any, formData: any) => {
        let buttonProps = {
            bsSize: 'sm',
        };

        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: `${this.appTextCommon('buttons.save')} ${Formatters.plural(this.appTextCommon('buttons.change'), this.getFormChangesCount(), false)}`,
            };
        }

        let deleteButtonProps = {
            bsStyle: 'danger',
            bsSize: 'sm',
            disabled: false,
            title: this.appTextCommon('buttons.delete'),
        };

        const curOrderIndex = _.findIndex(allOrders, (c) => Number(_.get(c, 'orderId', 0)) === Number(orderId));
        const firstOrderId = _.get(allOrders, '[0].orderId', orderId);
        const lastOrderId = _.get(allOrders, `[${_.size(allOrders) - 1}].orderId`, orderId);
        const prevOrderId = (curOrderIndex !== 0) ? _.get(allOrders, `[${curOrderIndex - 1}].orderId`, orderId) : lastOrderId;
        const nextOrderId = (curOrderIndex !== _.size(allOrders) - 1) ? _.get(allOrders, `[${curOrderIndex + 1}].orderId`, orderId) : firstOrderId;

        const activeLabel = `${curOrderIndex + 1} ${this.appTextCommon('buttons.of')} ${Formatters.plural(this.appText('order'), _.size(allOrders), true)}`;

        const pagination = (
            <Pagination bsSize="sm">
                <li>
                    <Link to={NavigationLinks.orderDetail(firstOrderId)}>
                        <i className="glyphicon glyphicon-step-backward" />
                    </Link>
                </li>
                <li>
                    <Link to={NavigationLinks.orderDetail(prevOrderId)}>
                        <i className="glyphicon glyphicon-chevron-left" />
                    </Link>
                </li>
                <Pagination.Item className="active">{activeLabel}</Pagination.Item>
                <li>
                    <Link to={NavigationLinks.orderDetail(nextOrderId)}>
                        <i className="glyphicon glyphicon-chevron-right" />
                    </Link>
                </li>
                <li>
                    <Link to={NavigationLinks.orderDetail(lastOrderId)}>
                        <i className="glyphicon glyphicon-step-forward" />
                    </Link>
                </li>
            </Pagination>
        );

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={9}>
                            <ButtonToolbar>
                                <Button {...deleteButtonProps} onClick={e => { this.handleDeleteButton(e); }}><i className="glyphicon glyphicon-trash" /> {deleteButtonProps.title}</Button>

                                <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleCreateOrderForAssocButton(e, formData); }}>
                                    <i className="glyphicon glyphicon-plus" /> {this.appText('new-order')}
                                </Button>

                                <Button {...buttonProps} bsStyle="info" onClick={e => { this.handleViewAssociationButton(e); }}>
                                    <i className="glyphicon glyphicon-eye-open" /> {this.appText('view-association')}
                                </Button>
                            </ButtonToolbar>
                        </Col>

                        <div>
                            <div className="pagination-container">{pagination}</div>
                        </div>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionActiveContactInformation(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputPropsReadonly = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            rowClassName: 'kendo-form-row-readonly'
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        const assocId = Formatters.getInt(formData, 'associationId');

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('active-contact-information')}>
                <Row>
                    <Col xs={5}>
                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('active-contact-association')}>
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.associationId"
                                label={this.appText('association-id')}
                                value={Formatters.getInt(formData, 'association.associationId')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.name"
                                label={this.appText('association-name')}
                                value={
                                    <div>
                                        <NavLinkComponent
                                            caption={_.get(formData, 'association.name')}
                                            linkDetails={{ to: NavigationLinks.associationDetail(assocId), title: this.appText('view-association') }}
                                        >
                                            {Formatters.getStr(formData, 'association.name')}
                                        </NavLinkComponent>
                                        <div>
                                            <CopyAddressToClipboard locationObj={_.get(formData, 'association.location')} />
                                        </div>
                                    </div>
                                }
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.associationType.name"
                                label={this.appText('association-type')}
                                value={Formatters.getStr(formData, 'association.associationType.name')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.location.address1"
                                label={this.appTextCommon('location.address')}
                                value={Formatters.getStr(formData, 'association.location.address1')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.location.city"
                                label={this.appTextCommon('location.city')}
                                value={Formatters.getStr(formData, 'association.location.city')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.location.stateCode"
                                label={this.appTextCommon('location.state')}
                                value={Formatters.getStr(formData, 'association.location.stateCode')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.location.postalCode"
                                label={this.appTextCommon('location.zip')}
                                value={Formatters.getStr(formData, 'association.location.postalCode')}
                                {...inputPropsReadonly}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>
                    </Col>

                    <Col xs={7}>
                        <DetailFormCommon.InventoryDetailFormSection title={this.appText('active-contact-client')}>
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.client.clientId"
                                label={this.appText('client-id')}
                                value={_.get(formData, 'association.client.clientId')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.client.name"
                                label={this.appText('client-name')}
                                value={
                                    <NavLinkComponent
                                        caption={_.get(formData, 'association.client.name')}
                                        linkDetails={{ to: NavigationLinks.clientDetail(_.get(formData, 'association.client.clientId')), title: this.appText('view-client') }}
                                    />
                                }
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.client.clientType.name"
                                label={this.appText('client-type')}
                                value={_.get(formData, 'association.client.clientType.name')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.client.phone"
                                label={this.appText('client-phone')}
                                value={_.get(formData, 'association.client.phone')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.contact.contactId"
                                label={this.appText('client-contact-id')}
                                value={_.get(formData, 'association.contact.contactId')}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="association.contact.contactId"
                                label={this.appText('client-manager')}
                                value={Formatters.composeClientContactFormal(_.get(formData, 'association.contact'))}
                                {...inputPropsReadonly}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_order.association.contact.email"
                                label={this.appText('client-contact-email')}
                                value={
                                    <EmailLinkComponent
                                        email={Formatters.getStr(formData, 'association.contact.email')}
                                        caption={`${this.appTextCommon('contacts.send-email-to')} ${Formatters.getStr(formData, 'association.contact.email')}`}
                                    />}
                                {...inputPropsReadonly}
                            />
                        </DetailFormCommon.InventoryDetailFormSection>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderOrderInformation(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsReadonly = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            rowClassName: 'kendo-form-row-readonly'
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
            colLabelXs: 5,
            colInputXs: 7,
        });

        let warnStalePriceDetected = (<div />);
        if (this.state.showWarningStaleAcceptedPrice) {
            warnStalePriceDetected = (
                <div className="edit-order-stale-accepted-price-warning">
                    <Alert bsStyle="warning">
                        <h4><i className="fa fa-exclamation-triangle" /> {this.appText('missing-data.warning.title')}</h4>
                        <p>{(this.state.showWarningMissingPricingData) ? this.appText('stale-accepted-price-and-missing-data.warning.body') : this.appText('stale-accepted-price.warning.body')}</p>
                    </Alert>
                </div>
            );
        }

        return (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-information')}>
                <Col xs={12}>
                    {warnStalePriceDetected}
                </Col>
                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.dash"
                    label={this.appText('order-number')}
                    value={formData.orderDashNumberText}
                    {...inputPropsReadonly}
                />

                { /*
                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.dash"
                    label={this.appText('order-number')}
                    value={formData.orderId}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.dash-let"
                    label={this.appText('order-dash-letter')}
                    value={formData.orderDashNumberLet}DEFAULT
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.dash-num"
                    label={this.appText('order-dash-number')}
                    value={formData.orderDashNumberNum}
                    {...inputPropsReadonly}
                />
                */ }

                <KendoWrappers.KendoDropdownListInput
                    name="company.companyId"
                    clearable={true}
                    label={this.appText('order-company')}
                    value={_.get(formData, 'company.companyId')}
                    data={formData.orderCompanyOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'company.companyId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelect}
                />

                <KendoWrappers.KendoDropdownListInput
                    name="status.statusId"
                    clearable={true}
                    label={this.appText('order-status')}
                    value={_.get(formData, 'status.statusId')}
                    data={formData.orderStatusOptions}
                    textField="text"
                    valueField="value"
                    // onChange={(o) => { this.handleFormInputChange({target: { name: 'status.statusId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                    onChange={(o) => {
                        this.handleChangeStatus({ target: { name: 'status.statusId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                    }}
                    {...inputPropsVirtualizedSelect}
                />

                <KendoWrappers.KendoDropdownListInput
                    name="serviceLevel.serviceLevelId"
                    clearable={true}
                    label={this.appText('order-selected-service')}
                    value={Formatters.getInt(formData, 'serviceLevel.serviceLevelId')}
                    data={this.modifyServiceLevelNames(formData.orderServiceOptions)}
                    textField="text"
                    valueField="value"
                    onChange={(o) => {
                        this.handleChangeServiceLevel({ target: { name: 'serviceLevel.serviceLevelId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                    }}
                    {...inputPropsVirtualizedSelect}
                />

                <KendoWrappers.KendoCurrencyInput
                    name="acceptedPrice"
                    clearable={true}
                    label={this.appText('order-accepted-price')}
                    value={formData.acceptedPrice}
                    {...inputProps}
                />

                <KendoWrappers.KendoCheckbox
                    name="overrideAcceptedPrice"
                    value={formData.overrideAcceptedPrice}
                    label={this.appText('order-override-accepted-price')}
                    // onChange={(o) => this.handleSetAcceptedPriceOverrideCheckbox(o)}
                    {...inputProps}
                />

                <KendoWrappers.KendoDropdownListInput
                    name="newUser.userObjectId"
                    clearable={true}
                    label={this.appText('order-manager-assigned')}
                    value={_.get(formData, 'user.userObjectId', '')}
                    data={formData.orderUserOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'user.userObjectId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelect}
                />

                <KendoWrappers.KendoDropdownListInput
                    name="newCoordinator.userObjectId"
                    clearable={true}
                    label={this.appText('order-coordinator-assigned')}
                    value={_.get(formData, 'siteInspector.userObjectId', '')}
                    data={formData.orderUserOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'siteInspector.userObjectId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelect}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );
    }

    public renderOrderClientInfo(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputPropsReadonly = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            rowClassName: 'kendo-form-row-readonly'
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        return (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-client-info')}>
                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.client.clientId"
                    label={this.appText('client-id')}
                    value={_.get(formData, 'client.clientId')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.client.name"
                    label={this.appText('client-name')}
                    value={
                        <NavLinkComponent
                            caption={_.get(formData, 'client.name')}
                            linkDetails={{ to: NavigationLinks.clientDetail(_.get(formData, 'client.clientId')), title: this.appText('view-client') }}
                        />
                    }
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.client.clientType.name"
                    label={this.appText('client-type')}
                    value={_.get(formData, 'client.clientType.name')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.contact.phone"
                    label={this.appText('client-phone')}
                    value={_.get(formData, 'client.phone')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.contact.contactId"
                    label={this.appText('client-contact-id')}
                    value={_.get(formData, 'contact.contactId')}
                    {...inputPropsReadonly}
                />

                <KendoWrappers.KendoDropdownListInput
                    name="contact.contactId"
                    clearable={true}
                    label={this.appText('client-manager')}
                    value={_.get(formData, 'contact.contactId')}
                    data={formData.orderOrderClientContactOptions}
                    textField="text"
                    valueField="value"
                    onChange={(o) => { this.handleFormInputChange({ target: { name: 'contact.contactId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                    {...inputPropsVirtualizedSelect}
                />

                <KendoWrappers.KendoDisabledInputHidden
                    name="_order.contact.email"
                    label={this.appText('client-contact-email')}
                    value={
                        <EmailLinkComponent
                            email={Formatters.getStr(formData, 'contact.email')}
                            caption={`${this.appTextCommon('contacts.send-email-to')} ${Formatters.getStr(formData, 'contact.email')}`}
                        />}
                    {...inputPropsReadonly}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );
    }

    public renderOrderPricing(formData: any) {
        // REGULAR ORDER PRICING (LEVELS 1, 2, 3)
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        const inputPropsPricingMatrix = _.merge({}, defaultInputProps, {
            colLabelXs: 0,
            colInputXs: 11,
        });

        let warnMissingDataDetected = (<div />);
        if (this.state.showWarningMissingPricingData) {
            warnMissingDataDetected = (
                <div className="edit-order-missing-data-warning">
                    <Alert bsStyle="warning">
                        <h4><i className="fa fa-exclamation-triangle" /> {this.appText('missing-data.warning.title')}</h4>
                        <p>{this.appText('missing-data.warning.body')}</p>
                    </Alert>
                </div>
            );
        }

        return _.concat(
            [(
                <Row key="pricing-matrix-header">
                    <Col xs={12}>
                        {warnMissingDataDetected}
                    </Col>
                    <Col xs={2} className="entity-detail-label">
                        <Button
                            type="button"
                            bsStyle="info"
                            bsSize="xs"
                            disabled={this.getInputDisabled('set-prices-button', formData)}
                            onClick={(e) => { this.handleShowModalCalculatePrices(e, formData); }}
                        >
                            <i className="fa fa-calculator" /> {this.appText('set-prices')}
                        </Button>
                    </Col>
                    <Col xs={10}>
                        <Row>
                            <Col xs={4} className="entity-detail-label"><strong>{this.appText('pricing-economy')}</strong></Col>
                            <Col xs={4} className="entity-detail-label"><strong>{this.appText('pricing-standard')}</strong></Col>
                            <Col xs={4} className="entity-detail-label"><strong>{this.appText('pricing-rush')}</strong></Col>
                        </Row>
                    </Col>
                </Row>
            )],
            _.get(formData, 'pricingMatrix', []).map((pricingLevel, i) => {
                const config = _.find(OrderPricing.PricingLevelConfig, { 'level': Formatters.getStr(pricingLevel, 'level') });

                const readOnly = _.get(config, 'readOnly', false);

                const plLevel = Formatters.getStr(pricingLevel, 'level');
                const isPricingLevel4 = (plLevel === '5') ? true : false;

                // console.log(`edit::renderOrderPricing i ${i} plLevel_pricingLevel.level ${plLevel}`, {
                //     pricingLevel,
                //     config,
                //     readOnly,
                //     isPricingLevel4,
                //     plLevel,
                // });

                if (isPricingLevel4) { return null; }

                const pricingMatrixLevelName = (isPricingLevel4) ?
                    `${this.appText('pricing-level')} ${this.appText('pricing-level-renamed-level5')}` :
                    `${this.appText('pricing-level')} ${pricingLevel.level}`;

                return (
                    <Row key={`order-pricing-matrix-${i}`} className={`order-edit-pricing-matrix-pricing-level-${pricingLevel.level}`}>
                        <Col xs={2} className="entity-detail-values">
                            <KendoWrappers.KendoDisabledInputHidden
                                name={`pricingMatrix-level-name-${i}`}
                                label=""
                                readOnly={readOnly}
                                disabled={readOnly}
                                value={pricingMatrixLevelName}
                                onChange={(n, v) => { this.handlePricingMatrixChange(n, pricingLevel); }}
                                {...inputPropsPricingMatrix}
                            />

                        </Col>

                        <Col xs={10}>
                            <Row>
                                <Col xs={4} className="entity-detail-values">
                                    <KendoWrappers.KendoCurrencyInput
                                        name={`pricingMatrix[${i}].economyPrice`}
                                        clearable={true}
                                        label=""
                                        readOnly={readOnly}
                                        disabled={readOnly}
                                        value={Formatters.getFloat(pricingLevel, 'economyPrice')}
                                        onChange={(n, v) => { this.handlePricingMatrixChange(n, pricingLevel); }}
                                        {...inputPropsPricingMatrix}
                                    />
                                </Col>
                                <Col xs={4} className="entity-detail-values">
                                    <KendoWrappers.KendoCurrencyInput
                                        name={`pricingMatrix[${i}].standardPrice`}
                                        clearable={true}
                                        label=""
                                        readOnly={readOnly}
                                        disabled={readOnly}
                                        value={Formatters.getFloat(pricingLevel, 'standardPrice')}
                                        onChange={(n, v) => { this.handlePricingMatrixChange(n, pricingLevel); }}
                                        {...inputPropsPricingMatrix}
                                    />
                                </Col>
                                <Col xs={4} className="entity-detail-values">
                                    <KendoWrappers.KendoCurrencyInput
                                        name={`pricingMatrix[${i}].rushPrice`}
                                        clearable={true}
                                        label=""
                                        readOnly={readOnly}
                                        disabled={readOnly}
                                        value={Formatters.getFloat(pricingLevel, 'rushPrice')}
                                        onChange={(n, v) => { this.handlePricingMatrixChange(n, pricingLevel); }}
                                        {...inputPropsPricingMatrix}
                                    />
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                );
            })
        );
    }

    public renderOrderPricing3YearPlan(formData: any): any {
        let result: any = _.stubArray();
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        const inputPropsPricingMatrixCurrency = _.merge({}, defaultInputProps, {
            colLabelXs: 0,
            colInputXs: 11,
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
            colLabelXs: 0,
            colInputXs: 11,
        });

        let inputPropsSmallDate = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handle3YearPlanPricingMatrixYearChange(n, v); },
            colInputXs: 12,
            className: 'order-edit-pricing-matrix-date-wrapper',
            formatPlaceholder: 'formatPattern',
        });

        const fdOSPM = _.get(formData, 'orderServicesPricingMatrix', {});
        const fdOSPMEconomy = _.get(fdOSPM, 'economyPricing');
        const fdOSPMStandard = _.get(fdOSPM, 'standardPricing');
        const fdOSPMRush = _.get(fdOSPM, 'rushPricing');

        // Determine if 3 year plan are is visible
        let fdOrderSelectedService = Formatters.getInt(formData, 'serviceLevelId');
        let fdOrderIs3YearPlan = false;
        switch (fdOrderSelectedService) {
            case OrderPricing.ServiceLevelConfig.economy.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.economy.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.economy.level4_3.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.standard.level4_3.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4_2.serviceLevelId:
            case OrderPricing.ServiceLevelConfig.rush.level4_3.serviceLevelId:
                fdOrderIs3YearPlan = true;
                break;
            default:
                fdOrderIs3YearPlan = false;
                break;
        }

        // 20200127: Always show "3 Year Plan" pricing section regardless of selected serviceLevel
        fdOrderIs3YearPlan = true;

        let hasFormChanges = this.getFormChangesCount();

        // console.log(`renderOrderPricing3YearPlan hasFormChanges ${hasFormChanges} fdOrderIs3YearPlan ${fdOrderIs3YearPlan}`, { fdOSPM, fc: this.state.formChanges, fdOrderSelectedService, formData });

        if (!fdOrderIs3YearPlan) { return (<div />); }

        let disabledButtonCreateNextPlanYear: boolean = true;
        disabledButtonCreateNextPlanYear = (hasFormChanges > 0) ? true : false; // false;

        // Create Next Plan Year Row
        let warnCreateNextPlanYearOrder = (<div />);
        if (disabledButtonCreateNextPlanYear) {
            warnCreateNextPlanYearOrder = (
                <div className="edit-order-3-year-plan-save-warning">
                    <Alert bsStyle="warning">
                        <h4><i className="fa fa-exclamation-triangle" /> {this.appText('pricing-3-year-plan-year-count.warning.title')}</h4>
                        <p>{this.appText('pricing-3-year-plan-year-count.warning.body')}</p>
                    </Alert>
                </div>
            );
        }

        let createNextPlanYearOrderButtonWrapper = (<div />);
        if (!disabledButtonCreateNextPlanYear) {
            createNextPlanYearOrderButtonWrapper = (
                <div className="create-next-plan-year-button-wrapper">
                    <Button
                        type="button"
                        bsStyle="info"
                        bsSize="xs"
                        disabled={disabledButtonCreateNextPlanYear}
                        readOnly={disabledButtonCreateNextPlanYear}
                        onClick={(e) => { this.handleShowModalCreateNextPlanYearOrder(e, formData); }}
                    >
                        <i className="fa fa-plus-square" /> {this.appText('pricing-3-year-plan-create-next-plan-year-order')}
                    </Button>
                </div>
            );
        }

        result.push(
            <Row key={`order-pricing-matrix-lev-L4-3yearplan-create-next-plan-year`} className={`order-edit-pricing-matrix-pricing-level-L4-3yearplan-pricing`}>
                <Col xs={2} className="entity-detail-values">
                    <KendoWrappers.KendoDisabledInputHidden
                        name={`pricingMatrix-level-name-L4`}
                        label=""
                        value={this.appText('pricing-3-year-plan-year-count')}
                        onChange={(n, v) => { this.handle3YearPlanPricingMatrixChange(n, 'L4'); }}
                        {...inputPropsPricingMatrixCurrency}
                    />
                </Col>

                <Col xs={10}>
                    <Row>
                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoNumberInput
                                name="order-pricing-year-count"
                                label=""
                                value={Formatters.getInt(fdOSPM, 'planYearSequenceNum')}
                                format="#"
                                clearable={true}
                                onChange={(n, v) => { this.handle3YearPlanPricingMatrixPlanYearChange(n, v); }}
                                {...inputPropsPricingMatrixCurrency}
                            />
                        </Col>

                        <Col xs={8} className="entity-detail-values">
                            {warnCreateNextPlanYearOrder}
                            {createNextPlanYearOrderButtonWrapper}
                        </Col>
                    </Row>
                </Col>
            </Row>
        );

        // Pricing currency input row
        result.push(
            <Row key={`order-pricing-matrix-lev-L4-3yearplan-pricing`} className={`order-edit-pricing-matrix-pricing-level-L4-3yearplan-pricing`}>
                <Col xs={2} className="entity-detail-values">
                    <KendoWrappers.KendoDisabledInputHidden
                        name={`pricingMatrix-level-name-L4`}
                        label=""
                        value={this.appText('pricing-3-year-plan-pricing')} // pricingMatrixLevelName}
                        onChange={(n, v) => { this.handle3YearPlanPricingMatrixChange(n, 'L4'); }}
                        {...inputPropsPricingMatrixCurrency}
                    />
                </Col>

                <Col xs={10}>
                    <Row>
                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoCurrencyInput
                                name={`orderServicesPricingMatrix.economyPricing.price`}
                                clearable={true}
                                label="Economy"
                                readOnly={false}
                                disabled={false}
                                value={Formatters.getFloat(fdOSPMEconomy, 'price')}
                                onChange={(n, v) => { this.handle3YearPlanPricingMatrixChange(n, 'L4'); }}
                                {...inputPropsPricingMatrixCurrency}
                            />
                        </Col>

                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoCurrencyInput
                                name={`orderServicesPricingMatrix.standardPricing.price`}
                                clearable={true}
                                label="Standard"
                                readOnly={false}
                                disabled={false}
                                value={Formatters.getFloat(fdOSPMStandard, 'price')}
                                onChange={(n, v) => { this.handle3YearPlanPricingMatrixChange(n, 'L4'); }}
                                {...inputPropsPricingMatrixCurrency}
                            />
                        </Col>

                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoCurrencyInput
                                name={`orderServicesPricingMatrix.rushPricing.price`}
                                clearable={true}
                                label="Rush"
                                readOnly={false}
                                disabled={false}
                                value={Formatters.getFloat(fdOSPMRush, 'price')}
                                onChange={(n, v) => { this.handle3YearPlanPricingMatrixChange(n, 'L4'); }}
                                {...inputPropsPricingMatrixCurrency}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        );

        // 3 Year Plan: Year Row
        result.push(
            <Row key={`order-pricing-matrix-lev-L4-3yp-year`} className={`order-edit-pricing-matrix-pricing-level-L4-3yp-year`}>
                <Col xs={2} className="entity-detail-values">
                    <KendoWrappers.KendoDisabledInputHidden
                        name={`pricingMatrix-level-name-L4-3yp-year1-header`}
                        label=""
                        readOnly={false}
                        disabled={false}
                        value={this.appText('pricing-3-year-plan-year')}
                        onChange={(n, v) => { this.handle3YearPlanPricingMatrixYearChange(n, 'L4'); }}
                        {...inputPropsPricingMatrixCurrency}
                    />
                </Col>

                <Col xs={10}>
                    <Row>
                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoDateInputOptional
                                name={`orderServicesPricingMatrix.economyPricing.pricingYearDate`}
                                clearable={true}
                                required={false}
                                disabled={false}
                                label=""
                                labelHelpTooltip=""
                                format="yyyy"
                                value={Formatters.formatDateForKendoDateInput(_.get(fdOSPMEconomy, 'pricingYearDate'))} // _.get(fdOSPMEconomy, 'pricingYearDate')}
                                {...inputPropsSmallDate}
                            />
                        </Col>

                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoDateInputOptional
                                name={`orderServicesPricingMatrix.standardPricing.pricingYearDate`}
                                clearable={true}
                                required={false}
                                disabled={false}
                                label=""
                                labelHelpTooltip=""
                                format="yyyy"
                                value={Formatters.formatDateForKendoDateInput(_.get(fdOSPMStandard, 'pricingYearDate'))}
                                {...inputPropsSmallDate}
                            />
                        </Col>

                        <Col xs={4} className="entity-detail-values">
                            <KendoWrappers.KendoDateInputOptional
                                name={`orderServicesPricingMatrix.rushPricing.pricingYearDate`}
                                clearable={true}
                                required={false}
                                disabled={false}
                                label=""
                                labelHelpTooltip=""
                                format="yyyy"
                                value={Formatters.formatDateForKendoDateInput(_.get(fdOSPMRush, 'pricingYearDate'))}
                                {...inputPropsSmallDate}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        );

        // 3 Year Plan: Level Due row
        result.push(
            <Row key={`order-pricing-matrix-lev-L4-3yp-level-due`} className={`order-edit-pricing-matrix-pricing-level-L4-3yp-level-due-row`}>
                <Col xs={2} className="entity-detail-values">
                    <KendoWrappers.KendoDisabledInputHidden
                        name={`pricingMatrix-level-name-L4-3yp-year1-leveldue-header`}
                        label=""
                        readOnly={false}
                        disabled={false}
                        value={this.appText('pricing-3-year-plan-level-due')}
                        onChange={(n, v) => { this.handle3YearPlanPricingMatrixLevelDueChange(n, ''); }}
                        {...inputPropsPricingMatrixCurrency}
                    />
                </Col>

                <Col xs={10}>
                    <Row>
                        <Col xs={4} className="_entity-detail-values">
                            <KendoWrappers.KendoDropdownListInput
                                name="orderServicesPricingMatrix.economyPricing.levelDueText"
                                label=""
                                value={_.get(fdOSPMEconomy, 'levelDueText')}
                                data={_.get(formData, 'orderServiceTypesOptions', [])}
                                textField="text"
                                valueField="value"
                                onChange={(o) => {
                                    this.handle3YearPlanPricingMatrixLevelDueChange({ target: { name: 'orderServicesPricingMatrix.economyPricing.levelDueText', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                                }}
                                {...inputPropsVirtualizedSelect}
                            />
                        </Col>

                        <Col xs={4} className="_entity-detail-values">
                            <KendoWrappers.KendoDropdownListInput
                                name="orderServicesPricingMatrix.standardPricing.levelDueText"
                                label=""
                                value={_.get(fdOSPMStandard, 'levelDueText')}
                                data={_.get(formData, 'orderServiceTypesOptions', [])}
                                textField="text"
                                valueField="value"
                                onChange={(o) => {
                                    this.handle3YearPlanPricingMatrixLevelDueChange({ target: { name: 'orderServicesPricingMatrix.standardPricing.levelDueText', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                                }}
                                {...inputPropsVirtualizedSelect}
                            />
                        </Col>

                        <Col xs={4} className="_entity-detail-values">
                            <KendoWrappers.KendoDropdownListInput
                                name="orderServicesPricingMatrix.rushPricing.levelDueText"
                                label=""
                                value={_.get(fdOSPMRush, 'levelDueText')}
                                data={_.get(formData, 'orderServiceTypesOptions', [])}
                                textField="text"
                                valueField="value"
                                onChange={(o) => {
                                    this.handle3YearPlanPricingMatrixLevelDueChange({ target: { name: 'orderServicesPricingMatrix.rushPricing.levelDueText', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o);
                                }}
                                {...inputPropsVirtualizedSelect}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        );

        return (
            <div className="order-edit-pricing-matrix order-edit-pricing-matrix-3yp">
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-pricing-3-year-plan')}>
                    {result}
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderOrderNotes(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        let inputPropsFullWidthOwnRow = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-notes')}>
                <DetailFormCommon.InventoryNoteRow key={`edit-order-note-row-notes-add`}>
                    <DetailFormCommon.InventoryNote
                        authorText={this.appText('order-notes-add-author-text')}
                        dateText=""
                        onClickDelete={(e, o) => this.handleFormInputChange(e, o)}
                        onClickSave={(e) => this.handleSaveButton(e)}
                        deleteButtonVisible={false}
                        saveButtonVisible={true}
                    >
                        <KendoWrappers.KendoTextarea
                            name="orderNotesAdd"
                            label=""
                            rows={2}
                            cols={40}
                            value={formData.orderNotesAdd}
                            {...inputPropsFullWidthOwnRow} />
                    </DetailFormCommon.InventoryNote>
                </DetailFormCommon.InventoryNoteRow>

                <DetailFormCommon.OrderNotes
                    name="orderNotesDelete"
                    orderNotes={formData.orderNotes}
                    onClickDelete={(e, o) => this.handleFormInputChange(e, o)}
                    onClickSave={(e) => this.handleSaveButton(e)}
                    deleteButtonVisible={true}
                    saveButtonVisible={false}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );
    }

    public renderOrderFollowUpNotes(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        let inputPropsFullWidthOwnRow = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-follow-up-notes')}>
                <DetailFormCommon.InventoryNoteRow key={`edit-order-note-row-notes-add`}>
                    <DetailFormCommon.InventoryNote
                        authorText={this.appText('order-notes-add-author-text')}
                        dateText=""
                        onClickDelete={(e, o) => this.handleFormInputChange(e, o)}
                        onClickSave={(e) => this.handleSaveButton(e)}
                        deleteButtonVisible={false}
                        saveButtonVisible={true}
                    >
                        <KendoWrappers.KendoTextarea
                            name="followUpNotesAdd"
                            label=""
                            rows={2}
                            cols={40}
                            value={formData.followUpNotesAdd}
                            {...inputPropsFullWidthOwnRow} />
                    </DetailFormCommon.InventoryNote>
                </DetailFormCommon.InventoryNoteRow>

                <DetailFormCommon.OrderFollowUpNotes
                    name="followUpNotesDelete"
                    orderFollowUpNotes={formData.followUpNotes}
                    onClickDelete={(e, o) => this.handleFormInputChange(e, o)}
                    onClickSave={(e) => this.handleSaveButton(e)}
                    deleteButtonVisible={true}
                    saveButtonVisible={false}
                />
            </DetailFormCommon.InventoryDetailFormSection>
        );
    }

    public getInputDisabled(inputName: string, formData: any) {
        let result = false;

        if (!inputName || !formData) { return result; }

        switch (inputName) {
            case 'startDate':
                result = (!_.get(formData, 'contractReceived', false));
                break;

            case 'set-due-date-button':
                result = (!Formatters.getStr(formData, 'startDate'));
                break;

            case 'set-prices-button':
                const pricingMatrix = _.get(formData, 'pricingMatrix', []);

                const pricingLev1 = OrderPricing.getOrderPricingLevel1Economy(pricingMatrix);
                const pricingLev2 = OrderPricing.getOrderPricingLevel2Economy(pricingMatrix);
                const pricingLev3 = OrderPricing.getOrderPricingLevel3Economy(pricingMatrix);

                result = ((pricingLev1 > 0) || (pricingLev2 > 0) || (pricingLev3 > 0)) ? false : true;
                break;

            case 'modal-new-order-button':
                result = (Formatters.getInt(formData, 'newOrder.associationId', -1) <= 0); // || (!Formatters.getInt(formData, 'newOrder.clientId')));
                break;

            case 'modal-set-prices-button':
                result = false;
                break;

            case 'modal-set-due-date-button':
                result = false;
                break;

            default:
                result = false;
                break;
        }

        return result;
    }

    public renderSectionOrderDetails(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 4 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const inputPropsDueDate = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('order-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            {this.renderOrderInformation(formData)}
                        </div>

                        <div>
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-dates')}>
                                <KendoWrappers.KendoDateInput
                                    name="bidDate"
                                    clearable={true}
                                    label={this.appText('order-bid-received-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.bidDate)}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoDateInput
                                    name="bidSentDate"
                                    clearable={true}
                                    label={this.appText('order-bid-sent-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.bidSentDate)}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoDateInput
                                    name="wonDate"
                                    clearable={true}
                                    label={this.appText('order-won-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.wonDate)}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoDateInput
                                    name="startDate"
                                    clearable={true}
                                    label={this.appText('order-start-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.startDate)}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoDateInput
                                    name="siDueDate"
                                    clearable={true}
                                    label={this.appText('order-si-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.siDueDate)}
                                    {...inputProps}
                                />

                                <KendoWrappers.KendoDateInput
                                    name="dueDate"
                                    clearable={true}
                                    label={this.appText('order-due-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.dueDate)}
                                    {...inputPropsDueDate}
                                >
                                    <div className="edit-order-set-due-date-button-wrapper">
                                        <Button
                                            disabled={this.getInputDisabled('set-due-date-button', formData)}
                                            bsSize="xs"
                                            bsStyle="info"
                                            onClick={e => { this.handleShowModalSetDueDate(e, formData); }}
                                        >
                                            <i className="glyphicon glyphicon-calendar" /> {this.appText('set-due-date')}
                                        </Button>
                                    </div>
                                </KendoWrappers.KendoDateInput>

                                <KendoWrappers.KendoDateInput
                                    name="completionDate"
                                    clearable={true}
                                    label={this.appText('order-completed-date')}
                                    value={Formatters.formatDateForKendoDateInput(formData.completionDate)}
                                    {...inputProps}
                                />
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        <div className="order-edit-pricing-matrix">
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-pricing')}>
                                {this.renderOrderPricing(formData)}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div>

                        {this.renderOrderPricing3YearPlan(formData)}
                        { /* <div className="order-edit-pricing-matrix order-edit-pricing-matrix-3yp">
                            <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-pricing-3-year-plan')}>
                                {this.renderOrderPricing3YearPlan(formData)}
                            </DetailFormCommon.InventoryDetailFormSection>
                        </div> */ }

                        <div>
                            {this.renderOrderNotes(formData)}
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderSectionOrderClientDetails(formData: any) {
        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('order-client-details')}>
                <Row>
                    <Col xs={12}>
                        <div>
                            {this.renderOrderClientInfo(formData)}
                        </div>
                    </Col>
                </Row>
            </WhiteBoxPageSection>
        );
    }

    public renderLinksButtonToolbar(formData: any) {
        return (
            <div className="order-edit-links text-center">
                <ButtonToolbar className="center-block">
                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleResearchBidsButton(e); }}>
                        <i className="fa fa-search" /> {this.appText('research-bids')}
                    </Button>

                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleOrderBidLetterPdfButton(e); }}>
                        <i className="fa fa-file" /> {this.appText('view-bid-letter')}
                    </Button>

                    <Button bsSize="xs" bsStyle="info" onClick={e => { this.handleOrderBidEnvelopePdfButton(e); }}>
                        <i className="fa fa-envelope" /> {this.appText('print-envelope')}
                    </Button>
                </ButtonToolbar>
            </div>
        );
    }

    public renderLinksOptions(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        const orderClientStateCode = Formatters.getStr(formData, 'association.location.stateCode');
        const isInCalifornia = (orderClientStateCode === OrderCommon.BidLetterStateCodes.California || Formatters.getInt(formData, 'company.companyId') === 2 || Formatters.getStr(formData, 'association.client.location.stateCode') === OrderCommon.BidLetterStateCodes.California);

        const bidLetterLanguageBlock = (isInCalifornia) ?
            <KendoWrappers.KendoCheckbox
                name="bidLetter3YearPlanLanguage"
                label={this.appText('bidLetter3YearPlanLanguage')}
                value={formData.bidLetter3YearPlanLanguage}
                {...inputProps}
            /> :
            '';

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-links-options')}>
                    {bidLetterLanguageBlock}

                    <KendoWrappers.KendoCheckbox
                        name="feedback"
                        label={this.appText('feedback')}
                        value={formData.feedback}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="sampleSent"
                        label={this.appText('sample-sent')}
                        value={formData.sampleSent}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="unsolicitedBid"
                        label={this.appText('unsolicited-bid')}
                        value={formData.unsolicitedBid}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="contractReceived"
                        label={this.appText('contract-received')}
                        value={formData.contractReceived}
                        {...inputProps}
                    />

                    {/* <KendoWrappers.KendoCheckbox
                        name="inspectorPaid"
                        label={this.appText('coordinator-paid')}
                        value={formData.inspectorPaid}
                        {...inputProps}
                    /> */}

                    <DetailFormCommon.InventoryDetailFormSectionSpacing />

                    <KendoWrappers.KendoCheckbox
                        name="enteredInQuickbooks"
                        label={this.appText('entered-in-quickbooks')}
                        value={formData.enteredInQuickbooks}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="invoiceSentInQuickbooks"
                        label={this.appText('invoice-sent-in-quickbooks')}
                        value={formData.invoiceSentInQuickbooks}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="inspectorPaid"
                        label={this.appText('coordinator-paid')}
                        value={formData.inspectorPaid}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="worksheetReceived"
                        label={this.appText('worksheet-received')}
                        value={formData.worksheetReceived}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="twoOptionPricing"
                        label={this.appText('use-two-option-pricing')}
                        value={formData.twoOptionPricing}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoCheckbox
                        name="isPreliminaryStudy"
                        label={this.appText('is-preliminary-study')}
                        value={formData.isPreliminaryStudy}
                        {...inputProps}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderLinksEventDates(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('order-links-event-dates')}>
                    <KendoWrappers.KendoDisabledInputHidden
                        name="preparedForFYEDateFormatted"
                        label={this.appText('prepared-for-fye')}
                        value={formData.preparedForFYEDateFormatted}
                        {...inputProps}
                    />
                    {/* <KendoWrappers.KendoDateInput
                        name="preparedForFYEDate"
                        clearable={true}
                        label={this.appText('prepared-for-fye')}
                        value={Formatters.formatDateForKendoDateInput(formData.preparedForFYEDate)}
                        {...inputProps}
                    /> */}

                    <KendoWrappers.KendoDateInput
                        name="followUpDate"
                        clearable={true}
                        label={this.appText('follow-up-date')}
                        value={Formatters.formatDateForKendoDateInput(formData.followUpDate)}
                        {...inputProps}
                    />

                    <KendoWrappers.KendoDateInput
                        name="reminderBidDate"
                        clearable={true}
                        label={this.appText('reminder-bid-date')}
                        value={Formatters.formatDateForKendoDateInput(formData.reminderBidDate)}
                        {...inputProps}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderSectionLinks(formData: any) {
        const defaultInputProps = { colLabelXs: 5, colInputXs: 7 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        return (
            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('order-links')}>
                <div>
                    {this.renderLinksButtonToolbar(formData)}
                </div>

                <div>
                    {this.renderLinksOptions(formData)}
                </div>

                <div>
                    {this.renderLinksEventDates(formData)}
                </div>

                <div>
                    {this.renderOrderFollowUpNotes(formData)}
                </div>

            </WhiteBoxPageSection>
        );
    }

    public renderNewOrderModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        return (
            <Modal enforceFocus={false} show={this.state.showModalNewOrder} onHide={() => this.handleCloseModalNewOrder()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.new-order.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div>
                        <DetailFormCommon.InventoryDetailFormSection title="">
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_current-association-id"
                                clearable={true}
                                label={this.appText('modals.new-order.current-association-id')}
                                value={Formatters.getInt(formData, 'associationId')}
                                {...inputPropsVirtualizedSelect}
                            />
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_current-association-name"
                                clearable={true}
                                label={this.appText('modals.new-order.current-association-name')}
                                value={Formatters.getStr(formData, 'association.name')}
                                {...inputPropsVirtualizedSelect}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            {/* <KendoWrappers.KendoDropdownListInputhandleSetPricesB
                                name="newOrder.associationId"
                                clearable={true}
                                filterable={true}
                                label={this.appText('new-order-association')}                                                                
                                value={Formatters.getInt(formData, 'newOrder.associationId')}
                                data={_.get(formData, 'newOrder.associationOptions', []).slice(this.state.skipAssociation, this.state.skipAssociation + this.virtualizedDropdownPageSize)}
                                onFilterChange={(e) => { this.handleAssociationDropdownFilterChange(e, formData); }}
                                required={true}
                                virtual={{
                                    total: _.size(_.get(formData, 'newOrder.associationOptions', [])),
                                    pageSize: this.virtualizedDropdownPageSize,
                                    skip: this.state.skipAssociation,
                                }}
                                onPageChange={(e) => { this.handleAssociationDropdownPageChange(e, formData); }}
                                textField="text"
                                valueField="value"                                
                                onChange={(o) => { this.handleFormInputChange({target: { name: 'newOrder.associationId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                                {...inputPropsVirtualizedSelect}
                            /> */}

                            {/* <KendoWrappers.KendoDropdownListInput
                                name="newOrder.clientId"
                                clearable={true}
                                label={this.appText('new-order-client')}
                                value={_.get(formData, 'newOrder.clientId')}
                                data={_.get(formData, 'newOrder.clientOptions', []).slice(this.state.skipClient, this.state.skipClient + this.virtualizedDropdownPageSize)}
                                // defaultItem={{ text: Formatters.getStr(formData, 'client.name'), value: _.get(formData, 'newOrder.clientId') }}
                                virtual={{
                                    total: _.size(_.get(formData, 'newOrder.clientOptions', [])),
                                    pageSize: this.virtualizedDropdownPageSize,
                                    skip: this.state.skipClient,
                                }}
                                onPageChange={(e) => { this.handleClientDropdownPageChange(e, formData); }}
                                textField="text"
                                valueField="value"
                                onChange={(o) => { this.handleFormInputChange({target: { name: 'newOrder.clientId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') }}, o); }}
                                {...inputPropsVirtualizedSelect}
                            /> */}
                        </DetailFormCommon.InventoryDetailFormSection>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        bsStyle="success"
                        disabled={this.getInputDisabled('modal-new-order-button', formData)}
                        onClick={(e) => this.handleCreateOrderOpenModalButton(e, formData)}
                    >
                        {this.appText('create-order')}
                    </Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalNewOrder()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderCalculatePricesModal = (formData: any) => {
        return (
            <Modal show={this.state.showModalCalculatePrices} onHide={() => this.handleCloseModalCalculatePrices()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.calculate-prices.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className="calculate-prices-modal-content">
                        <strong>{this.appText('modals.calculate-prices.header')}</strong>
                    </p>

                    <p className="calculate-prices-modal-content">
                        {this.appText('modals.calculate-prices.content')}
                    </p>

                    <p className="calculate-prices-modal-content">
                        <strong>{this.appText('modals.calculate-prices.are-you-sure')}</strong>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={this.getInputDisabled('modal-set-prices-button', formData)} onClick={(e) => this.handleSetPricesButton(e)}>{this.appText('set-prices')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalCalculatePrices()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderSetDueDateModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        const inputProps = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
        });

        let inputPropsFullWidth = _.merge({}, defaultInputProps, {
            onChange: (n, v) => { this.handleFormInputChange(n, v); },
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
        });

        const inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            className: 'inventory-detail-input-full-width',
        });

        return (
            <Modal show={this.state.showModalSetDueDate} onHide={() => this.handleCloseModalSetDueDate()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.set-due-date.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div>
                        <p className="calculate-prices-modal-content">Set due date to specified number of weeks from start date</p>

                        <DetailFormCommon.InventoryDetailFormSection title="">
                            <KendoWrappers.KendoDisabledInputHidden
                                name="_current-start-date"
                                clearable={true}
                                label={this.appText('order-start-date')}
                                value={Formatters.formatDateShortDate(_.get(formData, 'startData'))}
                                {...inputProps}
                            />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_current-due-date"
                                clearable={true}
                                label={this.appText('current-order-due-date')}
                                value={Formatters.formatDateShortDate(_.get(formData, 'dueDate'))}
                                {...inputProps}
                            />

                            <KendoWrappers.KendoNumberInput
                                name="setDueDateWeeks"
                                clearable={true}
                                label={this.appText('number-of-weeks')}
                                value={_.get(formData, 'setDueDateWeeks')}
                                min={1}
                                max={999}
                                step={1}
                                {...inputProps}
                            />

                            <DetailFormCommon.InventoryDetailFormSectionSpacing />

                            <KendoWrappers.KendoDisabledInputHidden
                                name="_new-due-date"
                                clearable={true}
                                label={this.appText('new-order-due-date')}
                                value={Formatters.formatDateShortDate(_.get(formData, 'newDueDate'))}
                                {...inputProps}
                            />

                        </DetailFormCommon.InventoryDetailFormSection>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={this.getInputDisabled('modal-set-due-date-button', formData)} onClick={(e) => this.handleSetDueDateButton(e)}>{this.appText('set-due-date')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalSetDueDate()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderCreateNextPlanYearOrderModal = (formData: any) => {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let modalContent = this.appText('modals.create-next-plan-year-order.content');

        const planSeqNum = Formatters.getInt(formData, 'orderServicesPricingMatrix.planYearSequenceNum');
        if (planSeqNum >= OrderPricing.OrderServicesPlanMaxSequence) {
            modalContent = this.appText('modals.create-next-plan-year-order.content-period-ended');
        }

        return (
            <Modal show={this.state.showModalCreateNextPlanYearOrder} onHide={() => this.handleCloseModalCreateNextPlanYearOrder()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.create-next-plan-year-order.title')}</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <p className="calculate-prices-modal-content">
                        <strong>{this.appText('modals.create-next-plan-year-order.header')}</strong>
                    </p>

                    <p className="calculate-prices-modal-content">
                        {modalContent}
                    </p>

                    <p className="calculate-prices-modal-content">
                        <strong>{this.appText('modals.create-next-plan-year-order.are-you-sure')}</strong>
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="success" disabled={false} onClick={(e) => this.handleCreateNextPlanYearOrderModalButton(e, formData)}>{this.appText('modals.create-next-plan-year-order.create-order')}</Button>
                    <Button bsStyle="danger" onClick={() => this.handleCloseModalCreateNextPlanYearOrder()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public renderModalDeleteOrder = () => {
        return (
            <Modal show={this.state.showModalDeleteOrder} onHide={() => this.handleCancelAndCloseModalDeleteOrder()}>
                <Modal.Header closeButton={true}>
                    <Modal.Title>{this.appText('modals.delete.title')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {this.appText('modals.delete.body')}
                </Modal.Body>
                <Modal.Footer>
                    <Button bsStyle="danger" onClick={(e) => { this.handleDeleteButtonAction(e); }}>{this.appTextCommon('buttons.delete')}</Button>
                    <Button onClick={() => this.handleCancelAndCloseModalDeleteOrder()}>{this.appTextCommon('buttons.cancel')}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    public getEditOrderPageTitle(appendStr: string = '', messagePath: string = 'page-title.edit-order', prependPath: boolean = true) {
        return this.appTextPageTitleAppendString(appendStr, messagePath, prependPath);
    }

    public render() {
        const { orderDetailOrder, orderChartDetail, orderId, orders, association, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes, ...rest } = this.props;

        const order = this.extendOrder(orderDetailOrder, orderChartDetail);
        const orderDetailOrderId = Formatters.getInt(orderDetailOrder, 'orderId');

        let pageTitle = this.getEditOrderPageTitle(orderId);

        let page = (<div />);

        if (Number(orderDetailOrderId) && Number(orderDetailOrderId) === Number(orderId) && _.size(users)) {
            const formData = this.getFormData(order, orderId, orders, users, statuses, serviceLevels, associationClient, orderClient, associations, clients, companies, orderServiceTypes);

            pageTitle = this.getEditOrderPageTitle(Formatters.getStr(formData, 'orderDashTextNumber'));

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    {this.renderSetDueDateModal(formData)}
                    {this.renderCalculatePricesModal(formData)}
                    {this.renderNewOrderModal(formData)}
                    {this.renderModalDeleteOrder()}
                    {this.renderCreateNextPlanYearOrderModal(formData)}

                    <Row>
                        <Col xs={12}>
                            {/* Header toolbar */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(order, orderId, orders, formData)}
                                    </div>
                                </Col>
                            </Row>

                            {/* Active contact section */}
                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderSectionActiveContactInformation(formData)}
                                    </div>
                                </Col>
                            </Row>

                            {/* Lower split section */}
                            <Row>
                                <Col xs={7} xl={6}>
                                    <div>
                                        {this.renderSectionOrderDetails(formData)}
                                    </div>
                                </Col>

                                <Col xs={5} xl={6}>
                                    <div>
                                        {this.renderSectionOrderClientDetails(formData)}
                                    </div>

                                    <div>
                                        {this.renderSectionLinks(formData)}
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col xs={12}>
                                    <div>
                                        {this.renderHeaderButtonToolbar(order, orderId, orders, formData)}
                                    </div>
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrl = props => ApiEndpoints.getEditOrderDetailUrl(props.match.params.orderId);

const mapPropsToConfigs = (props) => ReduxQueries.Queries.getEditOrderDetailRequest(
    getApiUrl(props),
    ReduxQueries.PropsAccessToken(props),
);

const mapStateToProps = (state, props) => {
    const orderId = _.get(props, 'match.params.orderId', 0);

    return ReduxQueries.MapQueryStateToPropsByUrl(getApiUrl(props), state, props, {
        orderId: orderId,
        orderDetailOrder: state.entities.editOrderDetailOrder,
        orderChartDetail: state.entities.editOrderDetailOrderChartDetail,
        orders: state.entities.editOrderDetailOrders,
        association: state.entities.editOrderDetailAssociation,
        associations: state.entities.editOrderDetailAssociations,
        clients: state.entities.editOrderDetailClients,
        associationClient: state.entities.editOrderDetailAssociationClient,
        orderClient: state.entities.editOrderDetailOrderClient,
        users: state.entities.editOrderDetailUsers,
        statuses: state.entities.editOrderDetailStatuses,
        orderServiceTypes: state.entities.editOrderDetailOrderServiceTypes,
        serviceLevels: state.entities.editOrderDetailServiceLevels,
        companies: state.entities.editOrderCompanies,
    });
};

const mapDispatchToProps = {
    updateOrder: MutateActions.updateOrderMutateAsync,
    deleteOrder: MutateActions.deleteOrderMutateAsync,
    newOrder: MutateActions.newOrderMutateAsync,
    createNext3YearPlanSeqOrder: MutateActions.createNext3YearPlanSeqOrderMutateAsync,
    navigateTo: (path) => push(path),
};

const EditOrderContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(EditOrder);

export default withRouter(EditOrderContainer);



// WEBPACK FOOTER //
// ./src/order/edit.tsx